/**
 * SummaryPanelContent - Contenido expandido del panel resumen
 * 
 * Componentes internos:
 * - DetailGrid: Comparativa detallada antes/después (estructura 3 columnas: ANTES | LABEL | DESPUÉS)
 * - ScoreDistribution: Barras horizontales de distribución de scores
 * - TypologyGrid: Grid 3x3 con 9 tipologías de infraestructura
 * 
 * Extraído de: P3_Descarga_v8_final.html (.panel-content, .detail-grid, .score-bars, .typology-grid)
 * 
 * @version 1.3.0
 * @date 2026-01-12
 * 
 * Cambios v1.3.0:
 * - Icono "Otros": Phosphor <Info size={20} /> + contraste mejorado (#71717A → #9CA3AF)
 * 
 * Cambios v1.2.0:
 * - ScoreBars: Tamaños igualados con Comparativa (labels 13px, values 13px weight 600)
 * 
 * Cambios v1.1.0:
 * - DetailGrid: Estructura 3 columnas (ANTES | LABEL | DESPUÉS) para evitar reiteración
 * - ScoreBars: Tamaños aumentados (labels 12px, values 13px)
 * - TypologyGrid: Tamaños aumentados (names 14px, counts 12px)
 */

import React from 'react';
import { Info } from '@phosphor-icons/react';
import { colors, typography, borderRadius } from '@/design-system/v2';

const FONT_SIZE_LG = '15px';

// ============================================================================
// TIPOS
// ============================================================================

export interface DetailStats {
  infraestructuras: number;
  conCoordenadas: number;
  coberturaPct: number;
  scorePromedio: number;
  erroresUtf8: number;
  coordsTruncadas: number;
}

export interface ScoreDistribution {
  high: number;    // ≥90
  medium: number;  // 70-89
  low: number;     // 50-69
  critical: number; // <50
}

export interface TypologyData {
  key: string;
  nombre: string;
  geolocalizadas: number;
  total: number;
}

export interface SummaryPanelContentProps {
  before: DetailStats;
  after: DetailStats;
  scoresBefore: ScoreDistribution;
  scoresAfter: ScoreDistribution;
  typologies: TypologyData[];
}

// ============================================================================
// ICONOS SVG
// ============================================================================

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v6l4 2"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const _ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

// Iconos de tipología (temporales - pendiente migrar a iconos oficiales PTEL)
const TypologyIcons: Record<string, React.FC> = {
  sanitario: () => (
    <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
  ),
  educativo: () => (
    <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
  ),
  seguridad: () => (
    <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
  ),
  servicios: () => (
    <svg viewBox="0 0 24 24" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
  ),
  cultural: () => (
    <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16"/></svg>
  ),
  deportivo: () => (
    <svg viewBox="0 0 24 24" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20M2 12h20"/></svg>
  ),
  transporte: () => (
    <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v5h-7"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
  ),
  administrativo: () => (
    <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
  ),
  otros: () => <Info size={20} />,
};

// ============================================================================
// COLORES TIPOLOGÍA (del HTML P3)
// ============================================================================

const TYPOLOGY_STYLES: Record<string, { bg: string; color: string }> = {
  sanitario: { bg: 'rgba(248, 113, 113, 0.15)', color: '#F87171' },
  educativo: { bg: 'rgba(96, 165, 250, 0.15)', color: '#60A5FA' },
  seguridad: { bg: 'rgba(163, 230, 53, 0.15)', color: '#A3E635' },
  servicios: { bg: 'rgba(251, 191, 36, 0.15)', color: '#FBBF24' },
  cultural: { bg: 'rgba(192, 132, 252, 0.15)', color: '#C084FC' },
  deportivo: { bg: 'rgba(45, 212, 191, 0.15)', color: '#2DD4BF' },
  transporte: { bg: 'rgba(251, 146, 60, 0.15)', color: '#FB923C' },
  administrativo: { bg: 'rgba(148, 163, 184, 0.15)', color: '#94A3B8' },
  otros: { bg: 'rgba(156, 163, 175, 0.15)', color: '#9CA3AF' },
};

// ============================================================================
// ESTILOS v1.2.0
// ============================================================================

/**
 * ESTILOS - Escala tipográfica v7+ (armonización P3)
 * 
 * Cambios v1.2.0:
 * - ScoreBars: labels y values igualados a 13px weight 600 (como Comparativa)
 * 
 * Cambios v1.1.0:
 * - DetailGrid: Nueva estructura 3 columnas sin reiteración de labels
 * - ScoreBars: labels 11→12px, values 11→13px
 * - TypologyGrid: names 13→14px, counts 11→12px
 */
const STYLES = {
  // ========== DETAIL GRID - CSS table: columnas valores se contraen al contenido ==========
  detailSection: {
    background: colors.bg.tertiary,
    borderRadius: borderRadius.xl,
    padding: '20px',
    marginBottom: '20px',
  },
  // Tabla completa
  detailTable: {
    display: 'table',
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  // Fila separadora bajo el header
  detailTableHead: {
    display: 'table-header-group',
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
  },
  detailTableBody: {
    display: 'table-row-group',
  },
  detailTableRow: {
    display: 'table-row',
  },
  // Celda valor (izq/der): se contrae al contenido, sin wrap
  detailCellValue: {
    display: 'table-cell',
    width: '1%',
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'middle',
    paddingBottom: '12px',
    paddingTop: '2px',
  },
  detailCellValueLeft: {
    textAlign: 'right' as const,
    paddingRight: '20px',
  },
  detailCellValueRight: {
    textAlign: 'left' as const,
    paddingLeft: '20px',
  },
  // Celda etiqueta central
  detailCellLabel: {
    display: 'table-cell',
    textAlign: 'center' as const,
    verticalAlign: 'middle',
    paddingBottom: '12px',
    paddingTop: '2px',
  },
  // Header cells (una sola fila)
  detailHeadCellValue: {
    display: 'table-cell',
    width: '1%',
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'bottom',
    paddingBottom: '10px',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
  },
  detailHeadCellLabel: {
    display: 'table-cell',
    textAlign: 'center' as const,
    verticalAlign: 'bottom',
    paddingBottom: '10px',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
    color: colors.fg.secondary,
  },
  detailValueBefore: {
    fontFamily: typography.fontFamily.mono,
    fontSize: FONT_SIZE_LG,
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.secondary,
  },
  detailLabel: {
    fontSize: typography.fontSize.base,
    color: colors.fg.secondary,
  },
  detailValueAfter: {
    fontFamily: typography.fontFamily.mono,
    fontSize: FONT_SIZE_LG,
    fontWeight: typography.fontWeight.semibold,
    color: colors.primary,
  },
  // legacy — no usado pero evita errores de referencia
  detailHeader: { display: 'none' },
  detailHeaderCol: {},
  detailHeaderBefore: {},
  detailHeaderLabel: {},
  detailHeaderAfter: {},
  detailHeaderIcon: {},
  detailRows: { display: 'none' },
  detailRow: { display: 'none' },
  detailArrowInline: {},
  detailArrowIcon: {},

  // ========== SCORE BARS - Tamaños igualados con Comparativa (v1.2.0) ==========
  scoreSection: {
    background: colors.bg.tertiary,
    borderRadius: borderRadius.xl,
    padding: '20px',
    marginBottom: '20px',
  },
  scoreHeader: {
    display: 'grid',
    gridTemplateColumns: '30% 40% 30%', // mismo ancho que tabla ORIGINAL/MÉTRICA/FINAL
    gap: '8px',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
  },
  scoreHeaderCol: {
    fontSize: typography.fontSize.sm, // 12px
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  scoreRows: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  scoreRow: {
    display: 'grid',
    gridTemplateColumns: '30% 40% 30%',
    gap: '8px',
    alignItems: 'center',
  },
  scoreBarContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  scoreBarLabel: {
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.base, // 13px - igualado con Comparativa (v1.2.0)
    fontWeight: typography.fontWeight.semibold, // 600
    width: '48px',
    textAlign: 'right' as const,
  },
  scoreLabelCenter: {
    fontSize: typography.fontSize.sm, // 12px
    color: colors.fg.secondary,
    textAlign: 'center' as const,
    fontWeight: typography.fontWeight.medium,
  },
  scoreBarTrack: {
    flex: 1,
    height: '10px',
    background: colors.bg.secondary,
    borderRadius: '5px',
    overflow: 'hidden',
  },
  scoreBarFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 0.3s ease',
  },
  scoreBarVal: {
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.base, // 13px - igualado con Comparativa (v1.2.0)
    fontWeight: typography.fontWeight.semibold, // 600 - igualado con Comparativa (v1.2.0)
    width: '36px',
  },

  // ========== TYPOLOGY GRID - Tamaños aumentados ==========
  typologySection: {
    background: colors.bg.tertiary,
    borderRadius: borderRadius.xl,
    padding: '20px',
  },
  typologyTitle: {
    fontSize: typography.fontSize.sm, // 12px
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.secondary,
    marginBottom: '18px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
  },
  typologyGrid: {
    display: 'grid',
    // auto-fill: ocupa todo el ancho disponible sin espacio vacío
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '12px',
  },
  typologyItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
    overflow: 'hidden',
  },
  typologyIcon: {
    width: '38px',
    height: '38px',
    minWidth: '38px',      // no se comprime
    borderRadius: borderRadius.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  typologyIconSvg: {
    width: '18px',
    height: '18px',
    stroke: 'currentColor',
    fill: 'none',
  },
  typologyInfo: {
    flex: 1,
    minWidth: 0,           // permite truncar texto
    overflow: 'hidden',
  },
  typologyName: {
    fontSize: '13px',
    fontWeight: typography.fontWeight.medium,
    color: colors.fg.primary,
    marginBottom: '3px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  typologyCount: {
    fontSize: '11px',
    color: colors.fg.muted,
    whiteSpace: 'nowrap' as const,
  },
  typologyCountHighlight: {
    fontFamily: typography.fontFamily.mono,
    fontWeight: typography.fontWeight.semibold,
    color: colors.primary, // teal — punto 1
  },
} as const;

// ============================================================================
// HELPERS
// ============================================================================

type ValueStyle = 'neutral' | 'good' | 'warn' | 'bad';

const getValueColor = (style: ValueStyle): string => {
  switch (style) {
    case 'good': return colors.primary;
    case 'warn': return colors.scores.low;
    case 'bad': return colors.scores.critical;
    default: return colors.fg.primary;
  }
};

const getScoreColor = (level: 'high' | 'medium' | 'low' | 'critical'): string => {
  return colors.scores[level];
};

// Datos de las filas de detalle
interface DetailRowData {
  label: string;
  beforeValue: string | number;
  afterValue: string | number;
  beforeStyle: ValueStyle;
  afterStyle: ValueStyle;
}

const getDetailRows = (before: DetailStats, after: DetailStats): DetailRowData[] => [
  {
    label: 'Infraestructuras',
    beforeValue: before.infraestructuras,
    afterValue: after.infraestructuras,
    beforeStyle: 'neutral',
    afterStyle: 'neutral',
  },
  {
    label: 'Con coordenadas',
    beforeValue: `${before.conCoordenadas} (${before.coberturaPct}%)`,
    afterValue: `${after.conCoordenadas} (${after.coberturaPct}%)`,
    beforeStyle: 'warn',
    afterStyle: 'good',
  },
  {
    label: 'Score promedio',
    beforeValue: before.scorePromedio,
    afterValue: after.scorePromedio,
    beforeStyle: 'warn',
    afterStyle: 'good',
  },
  {
    label: 'Errores UTF-8',
    beforeValue: before.erroresUtf8,
    afterValue: after.erroresUtf8,
    beforeStyle: 'bad',
    afterStyle: 'good',
  },
  {
    label: 'Coords truncadas',
    beforeValue: before.coordsTruncadas,
    afterValue: after.coordsTruncadas,
    beforeStyle: 'bad',
    afterStyle: 'good',
  },
];

// ============================================================================
// SUBCOMPONENTES
// ============================================================================

/** Grid de comparación detallada — CSS table para contracción real de columnas */
const DetailGrid: React.FC<{ before: DetailStats; after: DetailStats }> = ({ before, after }) => {
  const rows = getDetailRows(before, after);

  const headCell: React.CSSProperties = {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    paddingBottom: '10px',
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
    whiteSpace: 'nowrap',
  };

  return (
    <div style={STYLES.detailSection}>
      {/*
        Tabla de 3 columnas:
        - ORIGINAL (width:1%) y FINAL (width:1%) = se contraen al contenido
        - MÉTRICA = maxWidth fijo para que no se estire al ancho del contenedor
        El truco: la columna central tiene un maxWidth pequeño.
        paddingRight/Left reducido a 8px para pegar los valores a MÉTRICA.
      */}
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <colgroup>
      <col style={{ width: '30%' }} />{/* ORIGINAL */}
      <col style={{ width: '40%' }} />{/* MÉTRICA */}
               <col style={{ width: '30%' }} />{/* FINAL */}
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...headCell, color: colors.fg.muted, textAlign: 'right', paddingRight: '8px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: '12px', height: '12px', display: 'inline-block' }}><ClockIcon /></span>
                Original
              </span>
            </th>
            <th style={{ ...headCell, color: colors.fg.secondary, textAlign: 'center' }}>Métrica</th>
            <th style={{ ...headCell, color: colors.primary, textAlign: 'left', paddingLeft: '8px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                Final
                <span style={{ width: '12px', height: '12px', display: 'inline-block' }}><CheckCircleIcon /></span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              <td style={{
                ...STYLES.detailValueBefore,
                color: getValueColor(row.beforeStyle),
                textAlign: 'right',
                paddingRight: '8px',
                paddingTop: '12px',
                whiteSpace: 'nowrap',
              }}>
                {row.beforeValue}
              </td>
              <td style={{
                ...STYLES.detailLabel,
                textAlign: 'center',
                paddingTop: '12px',
              }}>
                {row.label}
              </td>
              <td style={{
                ...STYLES.detailValueAfter,
                color: getValueColor(row.afterStyle),
                textAlign: 'left',
                paddingLeft: '8px',
                paddingTop: '12px',
                whiteSpace: 'nowrap',
              }}>
                {row.afterValue}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Distribución de scores - Estructura 3 columnas */
const ScoreDistributionGrid: React.FC<{ 
  scoresBefore: ScoreDistribution; 
  scoresAfter: ScoreDistribution;
}> = ({ scoresBefore, scoresAfter }) => {
  const levels = ['high', 'medium', 'low', 'critical'] as const;
  const labels = { high: '≥90', medium: '70-89', low: '50-69', critical: '<50' };
  const rangeLabels = { high: 'Excelente', medium: 'Bueno', low: 'Regular', critical: 'Crítico' };
  
  return (
    <div style={STYLES.scoreSection}>
      {/* Header: alineación espejo de la tabla (antes=der, rango=centro, después=izq) */}
      <div style={STYLES.scoreHeader}>
        <div style={{ ...STYLES.scoreHeaderCol, justifyContent: 'flex-end', color: colors.fg.muted }}>
          Antes
          <span style={{ width: '14px', height: '14px', display: 'inline-block', marginLeft: '5px' }}><ClockIcon /></span>
        </div>
        <div style={{ ...STYLES.scoreHeaderCol, justifyContent: 'center', color: colors.fg.secondary }}>
          Rango
        </div>
        <div style={{ ...STYLES.scoreHeaderCol, justifyContent: 'flex-start', color: colors.primary }}>
          <span style={{ width: '14px', height: '14px', display: 'inline-block', marginRight: '5px' }}><CheckCircleIcon /></span>
          Después
        </div>
      </div>
      
      {/* Rows */}
      <div style={STYLES.scoreRows}>
        {levels.map(level => (
          <div key={level} style={STYLES.scoreRow}>
            {/* ANTES */}
            <div style={STYLES.scoreBarContainer}>
              <span style={{ ...STYLES.scoreBarLabel, color: getScoreColor(level) }}>
                {labels[level]}
              </span>
              <div style={STYLES.scoreBarTrack}>
                <div style={{
                  ...STYLES.scoreBarFill,
                  width: `${scoresBefore[level]}%`,
                  background: getScoreColor(level),
                }} />
              </div>
              <span style={{ ...STYLES.scoreBarVal, color: colors.fg.muted }}>
                {scoresBefore[level]}%
              </span>
            </div>
            
            {/* LABEL CENTRAL */}
            <div style={STYLES.scoreLabelCenter}>
              {rangeLabels[level]}
            </div>
            
            {/* DESPUÉS: misma dirección que ANTES, pegado al borde izq de la columna */}
            <div style={{ ...STYLES.scoreBarContainer }}>
              <span style={{ ...STYLES.scoreBarVal, color: colors.fg.muted }}>
                {scoresAfter[level]}%
              </span>
              <div style={STYLES.scoreBarTrack}>
                <div style={{
                  ...STYLES.scoreBarFill,
                  width: `${scoresAfter[level]}%`,
                  background: getScoreColor(level),
                }} />
              </div>
              <span style={{ ...STYLES.scoreBarLabel, color: getScoreColor(level), textAlign: 'left' as const }}>
                {labels[level]}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Grid de tipologías */
const TypologyGrid: React.FC<{ typologies: TypologyData[] }> = ({ typologies }) => {
  const totalInfras = typologies.reduce((s, t) => s + t.total, 0);
  const totalGeo    = typologies.reduce((s, t) => s + t.geolocalizadas, 0);

  return (
  <div style={STYLES.typologySection}>
    {/* Cabecera: título + totalizador */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <div style={{ ...STYLES.typologyTitle, marginBottom: 0 }}>Distribución por tipología</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.sm }}>
        <span style={{ color: colors.fg.muted, fontWeight: typography.fontWeight.semibold }}>{totalInfras}</span>
        <span style={{ color: colors.fg.subtle }}>/</span>
        <span style={{ color: colors.primary, fontWeight: typography.fontWeight.semibold }}>{totalGeo}</span>
        <span style={{ color: colors.fg.muted, fontSize: '11px' }}>geo</span>
      </div>
    </div>
    <div style={STYLES.typologyGrid}>
      {typologies.map(typ => {
        const style = TYPOLOGY_STYLES[typ.key] || TYPOLOGY_STYLES.otros;
        const Icon = TypologyIcons[typ.key] || TypologyIcons.otros;
        return (
          <div key={typ.key} style={STYLES.typologyItem}>
            <div style={{
              ...STYLES.typologyIcon,
              background: style.bg,
              color: style.color,
            }}>
              <span style={STYLES.typologyIconSvg}><Icon /></span>
            </div>
            <div style={STYLES.typologyInfo}>
              <div style={STYLES.typologyName}>{typ.nombre}</div>
              <div style={STYLES.typologyCount}>
                {/* total en gris, geolocalizadas en teal */}
                <span style={{ ...STYLES.typologyCountHighlight, color: colors.fg.muted }}>{typ.total}</span>
                <span style={{ color: colors.fg.subtle }}> / </span>
                <span style={{ ...STYLES.typologyCountHighlight, color: colors.primary }}>{typ.geolocalizadas}</span>
                <span style={{ color: colors.fg.muted }}> geo</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export const SummaryPanelContent: React.FC<SummaryPanelContentProps> = ({
  before,
  after,
  scoresBefore,
  scoresAfter,
  typologies,
}) => {
  return (
    <>
      {/* Comparativa detallada - Estructura 3 columnas */}
      <DetailGrid before={before} after={after} />

      {/* Distribución de scores - Estructura 3 columnas */}
      <ScoreDistributionGrid scoresBefore={scoresBefore} scoresAfter={scoresAfter} />

      {/* Tipologías */}
      <TypologyGrid typologies={typologies} />
    </>
  );
};

export default SummaryPanelContent;
